import React, { useState, useEffect } from "react";
import {
  AutocompleteInput,
  BooleanInput,
  CreateProps,
  FormDataConsumer,
  RadioButtonGroupInput,
  ReferenceInput,
  required,
  SaveButton,
  SelectInput,
  SimpleForm,
  TextInput,
  Toolbar,
  useDataProvider,
  useRedirect,
  useRefresh,
} from "react-admin";
import {
  Grid,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import {
  CcaAcademicYearResources,
  GetParam,
  sumCentsAmountToDolar,
} from "utils/tools";
import SanitizedGrid from "../../components/SanitizedGrid";
import CcaAcademicYearInput from "../../components/CcaAcademicYearInput";
import moment from "moment/moment";
import DaysOfWeekInput from "../../components/DaysOfWeekInput";
import AddressAutoCompleteInput from "../../components/AddressAutoCompleteInput";
import { calculationCostApproveCcaStudentRenewalWithScheduleValidation } from "utils/validation";
import FixedLocationDaysOfWeekInput from "../../components/FixedLocationDaysOfWeekInput";
import CalculationButton from "./components/CalculationButton";
import CustomCcaStudentScheduleDateInput from "../../components/CustomCcaStudentScheduleDateInput";
import CreateWrapper from "../../components/CreateWrapper";

const useStyles = makeStyles({
  table: {
    marginTop: "10px",
    minWidth: 650,
    padding: "1rem",
    marginBottom: "15px",
  },
});

const ApproveCcaStudentRenewalWithSchedule = (props: CreateProps) => {
  const redirect = useRedirect();
  const refresh = useRefresh();
  const param = GetParam();
  const provider = useDataProvider();
  const classes = useStyles();

  const format = "DD/MM/YY";

  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [isCalculationButtonClicked, setIsCalculationButtonClicked] =
    useState(false);
  const [school, setSchool] = useState<ISchool>();
  const [ccaDropOffType, setCcaDropOffType] = useState<string>();
  const [allowSelectWave, setAllowSelectWave] = useState<boolean>();
  const [isUseOneFixedLocation, setIsUseOneFixedLocation] =
    useState<boolean>(true);
  const [fixedLocations, setFixedLocations] = useState<
    ICcaFixedLocationDropOff[]
  >([]);
  const [resultCalculations, setResultCalculations] = useState<
    IResultCcaCalculation[]
  >([]);
  const [ccaAcademicTerms, setCcaAcademicTerms] = useState<ICcaAcademicTerm[]>(
    [],
  );
  const [ccaHolidays, setCcaHolidays] = useState<IHoliday[]>([]);

  const sourceParam = JSON.parse(param.get("source"));

  const ccaAcademicYearId = sourceParam?.ccaAcademicYearId ?? 0;
  const schoolId = sourceParam?.schoolId ?? 0;

  const ccaAcademicYearResources = CcaAcademicYearResources(ccaAcademicYearId);

  useEffect(() => {
    if (schoolId && schoolId > 0) {
      provider.getOne("schools", { id: Number(schoolId) }).then(({ data }) => {
        setSchool(data as ISchool);

        setAllowSelectWave(data.isAllowParentSelectWave);
        if (data.ccaDropOffType !== "both") {
          setCcaDropOffType(data.ccaDropOffType);
        }
      });

      provider
        .getList("ccaFixedLocationDropOffs", {
          filter: { SchoolId: Number(schoolId) },
          pagination: {
            page: 1,
            perPage: 1000,
          },
          sort: {
            field: "Id",
            order: "ASC",
          },
        })
        .then(({ data }) => {
          setFixedLocations(data as ICcaFixedLocationDropOff[]);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolId]);

  useEffect(() => {
    if (ccaAcademicYearId && ccaAcademicYearId > 0) {
      provider
        .getList("ccaAcademicTerms", {
          filter: { CcaAcademicYearId: Number(ccaAcademicYearId) },
          pagination: {
            page: 1,
            perPage: 1000,
          },
          sort: {
            field: "Id",
            order: "ASC",
          },
        })
        .then(({ data }) => {
          setCcaAcademicTerms(data as ICcaAcademicTerm[]);
        });

      provider
        .getList("ccaHolidays", {
          filter: { CcaAcademicYearId: Number(ccaAcademicYearId) },
          pagination: {
            page: 1,
            perPage: 1000,
          },
          sort: {
            field: "Id",
            order: "ASC",
          },
        })
        .then(({ data }) => {
          setCcaHolidays(data as IHoliday[]);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ccaAcademicYearId]);

  let ccaIncludeDates: IIncludeDates[] = [];
  ccaAcademicTerms.forEach((ccaAcademicTerm: ICcaAcademicTerm) => {
    let temp: IIncludeDates = {
      startDate: ccaAcademicTerm.startDate,
      endDate: ccaAcademicTerm.endDate,
    };

    ccaIncludeDates.push(temp);
  });

  let ccaExcludeDates: IExcludeDates[] = [];
  ccaHolidays.forEach((ccaHoliday: IHoliday) => {
    let temp: IExcludeDates = {
      startDate: ccaHoliday.date,
    };

    ccaExcludeDates.push(temp);
  });

  const breadcrumb = [
    {
      title: "CCA Student Renewals",
      link: "/ccaStudentRenewals",
      params: "",
    },
    {
      title: `#${param.get("ccaStudentRenewalId")}`,
      link: "",
      params: "",
    },
  ];

  const CustomRedirect = () => {
    redirect(`/ccaStudentRenewals`);
    refresh();
  };

  const CustomToolbar = (props: any) => (
    <Toolbar {...props}>
      <SaveButton
        disabled={
          props.invalid
            ? props.invalid
            : !isLoaded && !isCalculationButtonClicked
        }
        resource="ccaStudentSchedules"
      />
    </Toolbar>
  );

  const transform = async (data: any) => {
    const isFixedLocationMonday =
      data.isMonday && data.dropOffType === "fixed_location";
    const isFixedLocationTuesday =
      data.isTuesday && data.dropOffType === "fixed_location";
    const isFixedLocationWednesday =
      data.isWednesday && data.dropOffType === "fixed_location";
    const isFixedLocationThursday =
      data.isThursday && data.dropOffType === "fixed_location";
    const isFixedLocationFriday =
      data.isFriday && data.dropOffType === "fixed_location";

    data.startedAt = data.ccaStartedAt;
    data.endedAt = data.ccaEndedAt;

    let fixedLocationMondayId = data.fixedLocationMondayId;
    let fixedLocationTuesdayId = data.fixedLocationTuesdayId;
    let fixedLocationWednesdayId = data.fixedLocationWednesdayId;
    let fixedLocationThursdayId = data.fixedLocationThursdayId;
    let fixedLocationFridayId = data.fixedLocationFridayId;
    if (data.isUseOneFixedLocation === true) {
      fixedLocationMondayId = data.fixedLocationId;
      fixedLocationTuesdayId = data.fixedLocationId;
      fixedLocationWednesdayId = data.fixedLocationId;
      fixedLocationThursdayId = data.fixedLocationId;
      fixedLocationFridayId = data.fixedLocationId;
    }

    data.detail = {
      doorToDoorAddress:
        data.dropOffType === "door_to_door"
          ? {
              lat: data.ccaLocationData.LATITUDE,
              lng: data.ccaLocationData.LONGITUDE,
              street: data.street,
              unitNumber: data.unitNumber,
              blockNumber: data.blockNumber,
              postalCode: data.postalCode,
              building: data.building,
            }
          : null,
      isMonday: data.isMonday ? true : false,
      fixedLocationMondayId: isFixedLocationMonday
        ? fixedLocationMondayId
        : null,
      waveMondayId: isFixedLocationMonday ? data.waveMondayId : null,
      isTuesday: data.isTuesday ? true : false,
      fixedLocationTuesdayId: isFixedLocationTuesday
        ? fixedLocationTuesdayId
        : null,
      waveTuesdayId: isFixedLocationTuesday ? data.waveTuesdayId : null,
      isWednesday: data.isWednesday ? true : false,
      fixedLocationWednesdayId: isFixedLocationWednesday
        ? fixedLocationWednesdayId
        : null,
      waveWednesdayId: isFixedLocationWednesday ? data.waveWednesdayId : null,
      isThursday: data.isThursday ? true : false,
      fixedLocationThursdayId: isFixedLocationThursday
        ? fixedLocationThursdayId
        : null,
      waveThursdayId: isFixedLocationThursday ? data.waveThursdayId : null,
      isFriday: data.isFriday ? true : false,
      fixedLocationFridayId: isFixedLocationFriday
        ? fixedLocationFridayId
        : null,
      waveFridayId: isFixedLocationFriday ? data.waveFridayId : null,
      isMain: data.isMain && data.dropOffType === "door_to_door" ? true : false,
    };

    data.isRegisterCcaSchedule = true;

    delete data.dropOffTypeOption;
    delete data.locationData;
    delete data.street;
    delete data.blockNumber;
    delete data.postalCode;
    delete data.building;
    delete data.address;
    delete data.fixedLocationId;
    delete data.isMonday;
    delete data.fixedLocationMondayId;
    delete data.waveMondayId;
    delete data.isTuesday;
    delete data.fixedLocationTuesdayId;
    delete data.waveTuesdayId;
    delete data.isWednesday;
    delete data.fixedLocationWednesdayId;
    delete data.waveWednesdayId;
    delete data.isThursday;
    delete data.fixedLocationThursdayId;
    delete data.waveThursdayId;
    delete data.isFriday;
    delete data.fixedLocationFridayId;
    delete data.waveFridayId;
    delete data.date;
    delete data.isMainAddress;
    delete data.parentId;
    delete data.allowSelectWave;
    delete data.ccaStartedAt;
    delete data.ccaEndedAt;

    return data;
  };

  return (
    <>
      <CreateWrapper
        breadcrumb={breadcrumb}
        headerTitle="Approve CCA Renewal With Update CCA Schedule"
        transform={transform}
        onSuccess={CustomRedirect}
        {...props}
      >
        <SimpleForm toolbar={<CustomToolbar />}>
          <SanitizedGrid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h6">Student</Typography>
            </Grid>

            <Grid item xs={4}>
              <ReferenceInput
                label="Select Student"
                source="studentId"
                reference="students"
                validate={required()}
                disabled
              >
                <SelectInput
                  optionText={(choice: any) =>
                    `${choice.firstName} ${choice.lastName}`
                  }
                />
              </ReferenceInput>
            </Grid>
            <Grid item xs={8}></Grid>

            <Grid item xs={12}>
              <Typography variant="h6">School Details</Typography>
            </Grid>

            <Grid item xs={4}>
              <ReferenceInput
                label="School"
                source="schoolId"
                reference="schools"
                validate={required()}
                filterToQuery={(searchText) => {
                  if (searchText !== "") return { search: searchText };
                }}
                disabled
              >
                <AutocompleteInput optionText="name" optionId="id" />
              </ReferenceInput>
            </Grid>
            <Grid item xs={8}></Grid>

            <Grid item xs={4}>
              <CcaAcademicYearInput isDisabled={true} />
            </Grid>
            <Grid item xs={8}></Grid>

            <Grid item xs={12}>
              <Typography variant="h6">Schedule</Typography>
            </Grid>

            <Grid item xs={4}>
              <CustomCcaStudentScheduleDateInput
                source="ccaStartedAt"
                label="Start Date *"
                referenceSource={ccaAcademicYearResources}
                includeDates={ccaIncludeDates}
                excludeDates={ccaExcludeDates}
              />
            </Grid>
            <Grid item xs={8}></Grid>

            {school && school.ccaDropOffType === "both" && (
              <Grid item xs={12}>
                <RadioButtonGroupInput
                  label="Drop-off Address Type"
                  source="dropOffTypeOption"
                  choices={[
                    {
                      id: "fixed_location",
                      name: "Fixed Location",
                    },
                    {
                      id: "door_to_door",
                      name: "Door-to-door Location",
                    },
                  ]}
                  validate={required()}
                  onChange={(e) => {
                    setCcaDropOffType(e as unknown as string);
                  }}
                />
              </Grid>
            )}

            {ccaDropOffType === "fixed_location" && (
              <>
                <Grid item xs={12}>
                  <BooleanInput
                    source="isUseOneFixedLocation"
                    label="Use One Fixed Location for All Selected Days"
                    defaultValue={true}
                    onChange={(e: boolean) => {
                      setIsUseOneFixedLocation(e);
                    }}
                  />
                </Grid>

                {isUseOneFixedLocation && (
                  <>
                    <Grid item xs={12}>
                      <Typography variant="h6">Drop off Location</Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <SelectInput
                        source="fixedLocationId"
                        label="Select Fixed Location"
                        choices={fixedLocations.filter(
                          (fl) => fl.status === "active",
                        )}
                        validate={required()}
                      />
                    </Grid>
                    <Grid item xs={8}></Grid>
                  </>
                )}

                <Grid item xs={12}>
                  <Typography variant="h6">Days</Typography>
                </Grid>
                <Grid item xs={4}>
                  <FixedLocationDaysOfWeekInput
                    isUseOneFixedLocation={isUseOneFixedLocation}
                    allowSelectWave={allowSelectWave}
                    schoolId={school.id}
                  />
                </Grid>
              </>
            )}

            {ccaDropOffType === "door_to_door" && (
              <>
                <DaysOfWeekInput />

                <Grid item xs={12}>
                  <Typography variant="h6">
                    Pick up & Drop off Address
                  </Typography>
                </Grid>

                <Grid item xs={4}>
                  <AddressAutoCompleteInput
                    label="Postal Code *"
                    source="postalCode"
                    referenceSource="ccaLocationData"
                  />
                </Grid>
                <Grid item xs={8}></Grid>

                <Grid item xs={2}>
                  <TextInput label="Unit No." source="unitNumber" />
                </Grid>
                <Grid item xs={2}>
                  <TextInput label="Block No." source="blockNumber" />
                </Grid>
                <Grid item xs={8}></Grid>

                <Grid item xs={4}>
                  <TextInput label="Building Name" source="building" />
                </Grid>
                <Grid item xs={8}></Grid>

                <Grid item xs={4}>
                  <TextInput
                    label="Street"
                    source="street"
                    validate={required()}
                  />
                </Grid>
                <Grid item xs={8}></Grid>

                <Grid item xs={4}>
                  <BooleanInput
                    label="Set this address as main address."
                    source="isMain"
                  />
                </Grid>
                <Grid item xs={8}></Grid>
              </>
            )}

            {isLoaded && (
              <Grid item xs={12}>
                {resultCalculations.length > 0 ? (
                  <div style={{ marginTop: -20 }}>
                    {resultCalculations.map(
                      (
                        resultCalculation: IResultCcaCalculation,
                        index: number,
                      ) => (
                        <div key={`result-${index}`}>
                          <Grid item xs={12}>
                            <Typography variant="h6">
                              Cost Preview for Rate Chart #
                              {resultCalculation.ccaRateChart.id} -{" "}
                              {resultCalculation.ccaRateChart.name}
                            </Typography>

                            {resultCalculation.transactions.length > 0 ? (
                              <>
                                <TableContainer>
                                  <Table className={classes.table} size="small">
                                    <TableHead>
                                      <TableRow>
                                        <TableCell>Schedule Date</TableCell>
                                        <TableCell>Amount</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {resultCalculation.transactions.map(
                                        (
                                          item: IResultCalculationItem,
                                          key: number,
                                        ) => {
                                          return (
                                            <TableRow
                                              key={`transaction-${key}`}
                                            >
                                              <TableCell>
                                                {moment
                                                  .utc(item.fromDate)
                                                  .format(format)}{" "}
                                                -{" "}
                                                {moment
                                                  .utc(item.toDate)
                                                  .format(format)}
                                              </TableCell>
                                              <TableCell>
                                                ${" "}
                                                {(item.total / 100).toFixed(2)}
                                              </TableCell>
                                            </TableRow>
                                          );
                                        },
                                      )}

                                      <TableRow>
                                        <TableCell>Total :</TableCell>
                                        <TableCell>
                                          ${" "}
                                          {sumCentsAmountToDolar(
                                            resultCalculation.transactions,
                                          )}
                                        </TableCell>
                                      </TableRow>
                                    </TableBody>
                                  </Table>
                                </TableContainer>
                              </>
                            ) : (
                              <Grid item xs={12}>
                                <Typography variant="h6">
                                  No Change in Cost.
                                </Typography>
                              </Grid>
                            )}
                          </Grid>
                        </div>
                      ),
                    )}
                  </div>
                ) : (
                  <Grid item xs={12}>
                    <Typography variant="h6">No Change in Cost.</Typography>
                  </Grid>
                )}
              </Grid>
            )}

            <Grid item xs={12}>
              <FormDataConsumer>
                {({ formData }) => {
                  formData.dropOffType = ccaDropOffType;
                  formData.allowSelectWave = allowSelectWave;
                  formData.ccaEndedAt = moment
                    .utc(ccaAcademicYearResources?.endDate)
                    .format("yyyy-MM-DD");
                  return (
                    <div>
                      <CalculationButton
                        invalid={calculationCostApproveCcaStudentRenewalWithScheduleValidation(
                          formData,
                        )}
                        setResultCalculations={setResultCalculations}
                        setIsLoaded={setIsLoaded}
                        setIsCalculationButtonClicked={
                          setIsCalculationButtonClicked
                        }
                        formData={formData}
                      />
                    </div>
                  );
                }}
              </FormDataConsumer>
            </Grid>
          </SanitizedGrid>
        </SimpleForm>
      </CreateWrapper>
    </>
  );
};

export default ApproveCcaStudentRenewalWithSchedule;
